// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * compress.ts — main-thread controller. Owns the worker, drives a sequential
 * job queue (decode-then-encode for new files, encode-only for setting tweaks),
 * and emits per-item + overall progress to the UI.
 */

import { FORMATS, toOutputName, type OutputFormat } from './image';
import type { CompressItem, WorkerMessage, WorkerRequest } from './types';

export interface CompressSettings {
  format: OutputFormat;
  quality: number;
  /** Longest-edge cap in px; 0 = no resize. */
  maxDim: number;
}

export interface CompressHooks {
  onItem: (item: CompressItem) => void;
  onOverall: (done: number, total: number, bytesPerSec: number) => void;
  onLog: (level: 'info' | 'ok' | 'warn' | 'err', msg: string, meta?: Record<string, string | number>) => void;
  onIdle: () => void;
}

interface Job {
  id: number;
  kind: 'process' | 'reencode';
}

let nextId = 1;

export class Compressor {
  private worker: Worker;
  private hooks: CompressHooks;
  private settings: CompressSettings;

  private queue: Job[] = [];
  private byId = new Map<number, CompressItem>();
  private files = new Map<number, File>();
  private decoded = new Set<number>();
  private active: Job | null = null;

  private ready = false;
  private pending: (() => void)[] = [];

  // overall progress accounting for the current wave of jobs
  private total = 0;
  private done = 0;
  private bytesDone = 0;
  private startedAt = 0;

  constructor(hooks: CompressHooks, settings: CompressSettings) {
    this.hooks = hooks;
    this.settings = settings;
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.worker.addEventListener('message', (ev: MessageEvent<WorkerMessage>) => this.onMessage(ev.data));
    this.worker.addEventListener('error', (ev) => {
      this.hooks.onLog('err', `Worker crashed: ${ev.message}`);
      if (this.active) this.fail(this.active.id, 'The compression worker crashed. Try reloading.');
    });
  }

  getSettings(): CompressSettings {
    return this.settings;
  }

  /** Ingest files; anything that isn't image-ish is skipped with a log line. */
  async add(files: File[]): Promise<void> {
    let accepted = 0;
    for (const file of files) {
      if (file.type && !file.type.startsWith('image/')) {
        this.hooks.onLog('warn', `Skipped ${file.name || 'file'} — not an image.`, { name: file.name });
        continue;
      }
      const id = nextId++;
      const item: CompressItem = {
        id,
        name: file.name || 'image',
        inputSize: file.size,
        inputType: file.type || 'image/*',
        status: 'queued',
        srcUrl: URL.createObjectURL(file),
      };
      this.byId.set(id, item);
      this.files.set(id, file);
      this.hooks.onItem(item);
      this.hooks.onLog('info', `Queued ${item.name}`, { size: item.inputSize });
      this.enqueue({ id, kind: 'process' });
      accepted += 1;
    }
    if (accepted > 0) this.startWaveIfIdle();
  }

  /** Apply new settings and re-encode every decoded image (cheap — no re-decode). */
  setSettings(s: CompressSettings): void {
    this.settings = s;
    const targets = [...this.byId.values()].filter((i) => i.status === 'done' || i.status === 'error');
    if (targets.length === 0) return;
    for (const item of targets) {
      this.enqueue({ id: item.id, kind: this.decoded.has(item.id) ? 'reencode' : 'process' });
    }
    this.startWaveIfIdle();
  }

  private enqueue(job: Job): void {
    // Coalesce: never queue the same id twice.
    if (this.active?.id === job.id) return;
    if (this.queue.some((j) => j.id === job.id)) return;
    this.queue.push(job);
    this.total += 1;
  }

  private startWaveIfIdle(): void {
    if (!this.active && this.done >= this.total) {
      // Fresh wave — reset accounting so the overall bar starts at 0.
      this.total = this.queue.length;
      this.done = 0;
      this.bytesDone = 0;
      this.startedAt = performance.now();
    }
    this.emitOverall();
    this.pump();
  }

  private pump(): void {
    if (this.active) return;
    const job = this.queue.shift();
    if (!job) {
      this.hooks.onIdle();
      return;
    }
    const item = this.byId.get(job.id);
    if (!item) {
      this.pump();
      return;
    }
    this.active = job;
    const outName = toOutputName(item.name, FORMATS[this.settings.format].ext);
    item.outName = outName;
    item.status = job.kind === 'process' ? 'decoding' : 'encoding';
    this.hooks.onItem(item);

    const send = () => {
      if (job.kind === 'reencode') {
        this.post({
          type: 'reencode',
          id: job.id,
          outName,
          format: this.settings.format,
          quality: this.settings.quality,
          maxDim: this.settings.maxDim,
        });
      } else {
        void this.sendProcess(job.id, outName);
      }
    };
    if (this.ready) send();
    else this.pending.push(send);
  }

  private async sendProcess(id: number, outName: string): Promise<void> {
    const file = this.files.get(id);
    if (!file) {
      this.fail(id, 'Original file is no longer available.');
      return;
    }
    let buffer: ArrayBuffer;
    try {
      buffer = await file.arrayBuffer();
    } catch {
      this.fail(id, 'Could not read the file.');
      return;
    }
    this.post(
      {
        type: 'process',
        id,
        name: file.name,
        outName,
        format: this.settings.format,
        quality: this.settings.quality,
        maxDim: this.settings.maxDim,
        buffer,
      },
      [buffer],
    );
  }

  private post(msg: WorkerRequest, transfer?: Transferable[]): void {
    this.worker.postMessage(msg, transfer ?? []);
  }

  private onMessage(msg: WorkerMessage): void {
    switch (msg.type) {
      case 'ready': {
        this.ready = true;
        for (const fn of this.pending.splice(0)) fn();
        break;
      }
      case 'progress': {
        const item = this.byId.get(msg.id);
        if (!item) return;
        item.status = msg.phase === 'decode' ? 'decoding' : 'encoding';
        this.hooks.onItem(item);
        break;
      }
      case 'cache-miss': {
        // Worker lost the decoded pixels (e.g. after a reset) — re-decode.
        if (this.active?.id === msg.id) {
          const item = this.byId.get(msg.id);
          void this.sendProcess(msg.id, item?.outName ?? 'image');
        }
        break;
      }
      case 'done': {
        const item = this.byId.get(msg.id);
        if (!item) return;
        this.decoded.add(msg.id);
        if (item.outUrl) URL.revokeObjectURL(item.outUrl);
        item.status = 'done';
        item.blob = msg.blob;
        item.outName = msg.outName;
        item.outputSize = msg.blob.size;
        item.width = msg.width;
        item.height = msg.height;
        item.srcWidth = msg.srcWidth;
        item.srcHeight = msg.srcHeight;
        item.outUrl = URL.createObjectURL(msg.blob);
        item.error = undefined;
        this.bytesDone += item.inputSize;
        this.hooks.onItem(item);
        this.hooks.onLog('ok', `Compressed ${item.name} → ${item.outName}`, {
          out: item.outName ?? '',
          size: item.outputSize ?? 0,
        });
        this.finishActive();
        break;
      }
      case 'error': {
        this.fail(msg.id, msg.message);
        break;
      }
    }
  }

  private fail(id: number, message: string): void {
    const item = this.byId.get(id);
    if (item) {
      item.status = 'error';
      item.error = message;
      this.hooks.onItem(item);
      this.hooks.onLog('err', `Failed ${item.name}: ${message}`, { name: item.name });
    }
    this.finishActive();
  }

  private finishActive(): void {
    this.active = null;
    this.done += 1;
    this.emitOverall();
    this.pump();
  }

  private emitOverall(): void {
    const elapsed = this.startedAt ? (performance.now() - this.startedAt) / 1000 : 0;
    const bps = elapsed > 0 ? this.bytesDone / elapsed : 0;
    this.hooks.onOverall(this.done, this.total, bps);
  }

  items(): CompressItem[] {
    return [...this.byId.values()];
  }

  successful(): CompressItem[] {
    return this.items().filter((i) => i.status === 'done' && i.blob);
  }

  get(id: number): CompressItem | undefined {
    return this.byId.get(id);
  }

  isBusy(): boolean {
    return this.active !== null || this.queue.length > 0;
  }

  remove(id: number): void {
    const item = this.byId.get(id);
    if (item?.outUrl) URL.revokeObjectURL(item.outUrl);
    if (item?.srcUrl) URL.revokeObjectURL(item.srcUrl);
    this.byId.delete(id);
    this.files.delete(id);
    this.decoded.delete(id);
    this.queue = this.queue.filter((j) => j.id !== id);
    this.post({ type: 'forget', id });
  }

  reset(): void {
    for (const item of this.byId.values()) {
      if (item.outUrl) URL.revokeObjectURL(item.outUrl);
      if (item.srcUrl) URL.revokeObjectURL(item.srcUrl);
    }
    this.queue = [];
    this.byId.clear();
    this.files.clear();
    this.decoded.clear();
    this.active = null;
    this.total = 0;
    this.done = 0;
    this.bytesDone = 0;
    this.startedAt = 0;
    this.post({ type: 'forget' });
  }

  destroy(): void {
    this.reset();
    this.worker.terminate();
  }
}
